import { apiService } from './api';

// ─── DTOs ─────────────────────────────────────────────────────────────────────

export interface WarehouseStockBreakdownDTO {
  warehouseId: number;
  warehouseName: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
}

export interface InventoryStockSummaryItemDTO {
  productId: number;
  sku: string;
  productName: string;
  categoryName: string | null;
  totalQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  reorderLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  unitCost: number;
  totalValue: number;
  byWarehouse: WarehouseStockBreakdownDTO[];
}

export interface InventoryStockSummaryDTO {
  totalProducts: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalStockValue: number;
  items: InventoryStockSummaryItemDTO[];
}

export interface LowStockItemDTO {
  productId: number;
  sku: string;
  productName: string;
  categoryName: string | null;
  availableQuantity: number;
  reorderLevel: number;
  reorderQuantity: number;
  shortage: number;
  isOutOfStock: boolean;
  warehouseNames: string | null;
}

export interface DeadStockItemDTO {
  productId: number;
  sku: string;
  productName: string;
  categoryName: string | null;
  totalQuantity: number;
  unitCost: number;
  totalValue: number;
  lastMovementDate: string | null;
  daysSinceLastMovement: number;
}

export interface StockValuationItemDTO {
  productId: number;
  sku: string;
  productName: string;
  categoryName: string | null;
  quantity: number;
  unitCost: number;
  totalValue: number;
  warehouseName: string | null;
}

export interface StockValuationSummaryDTO {
  totalValue: number;
  totalProducts: number;
  totalUnits: number;
  items: StockValuationItemDTO[];
}

export interface StockMovementReportDTO {
  id: number;
  productId: number;
  productName: string;
  productSKU: string;
  warehouseName: string | null;
  movementType: string;
  quantity: number;
  relatedDocumentType: string | null;
  relatedDocumentId: number | null;
  notes: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface GRNReportItemDTO {
  grnId: number;
  grnNumber: string;
  grnDate: string;
  supplierName: string;
  warehouseName: string;
  grnType: string;
  status: string;
  approvalStatus: string | null;
  totalItems: number;
  totalQuantityReceived: number;
  totalValue: number;
  purchaseOrderNumber: string | null;
}

export interface StockAdjustmentReportDTO {
  id: number;
  documentNumber: string;
  productName: string;
  productSKU: string;
  warehouseName: string;
  previousQuantity: number;
  newQuantity: number;
  quantityChanged: number;
  reason: string;
  notes: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface OpeningClosingStockItemDTO {
  productId: number;
  sku: string;
  productName: string;
  categoryName: string | null;
  openingQuantity: number;
  inQuantity: number;
  outQuantity: number;
  adjustmentQuantity: number;
  closingQuantity: number;
  unitCost: number;
  openingValue: number;
  closingValue: number;
}

export interface OpeningClosingStockDTO {
  periodStart: string;
  periodEnd: string;
  totalInQty: number;
  totalOutQty: number;
  totalAdjustmentQty: number;
  openingValue: number;
  closingValue: number;
  items: OpeningClosingStockItemDTO[];
}

// ─── Params ───────────────────────────────────────────────────────────────────

export interface InventoryReportParams {
  startDate?: string;
  endDate?: string;
  warehouseId?: number;
  productId?: number;
  categoryId?: number;
  days?: number;
  limit?: number;
}

// ─── Service ──────────────────────────────────────────────────────────────────

const BASE = '/reports/inventory';

export const inventoryReportService = {
  getStockSummary: (params?: InventoryReportParams) =>
    apiService.get<InventoryStockSummaryDTO>(`${BASE}/stock-summary`, params ?? {}),

  getLowStock: (params?: InventoryReportParams) =>
    apiService.get<LowStockItemDTO[]>(`${BASE}/low-stock`, params ?? {}),

  getDeadStock: (params?: InventoryReportParams) =>
    apiService.get<DeadStockItemDTO[]>(`${BASE}/dead-stock`, params ?? {}),

  getValuation: (params?: InventoryReportParams) =>
    apiService.get<StockValuationSummaryDTO>(`${BASE}/valuation`, params ?? {}),

  getMovements: (params?: InventoryReportParams) =>
    apiService.get<StockMovementReportDTO[]>(`${BASE}/movements`, params ?? {}),

  getGRNReport: (params?: InventoryReportParams) =>
    apiService.get<GRNReportItemDTO[]>(`${BASE}/grn`, params ?? {}),

  getAdjustments: (params?: InventoryReportParams) =>
    apiService.get<StockAdjustmentReportDTO[]>(`${BASE}/adjustments`, params ?? {}),

  getOpeningClosing: (params?: InventoryReportParams) =>
    apiService.get<OpeningClosingStockDTO>(`${BASE}/opening-closing`, params ?? {}),
};
